"use client";

import { Identifier } from "@/types/domain";
import { Locale } from "@/types/locale";
import React, { useCallback, useMemo } from "react";
import { AlertDialogWrapper } from "@components/common/containers/alert-dialog-wrapper";
import { localizeFrom, useAnyTranslations } from "@lib/i18n/utils";
import { ProgramProposalEntity } from "@features/events-program/types/program-proposal";
import { TeamMemberAggregateEntity } from "@features/teams/types/team";
import { ActivityMemberEntity } from "@features/proposals/types/member";
import { ProposalVoteEntity } from "@features/events-program/types/proposal-vote";
import { ProposalVoteFormController } from "./proposal-vote-form";
import {
  buildProposalVoteFormRemountKey,
  ProposalVoteFormEntryIntent,
} from "./proposal-vote-form-model";

interface ProposalVoteDialogProps {
  open: boolean;
  proposal?: ProgramProposalEntity;
  teamAggregates?: TeamMemberAggregateEntity[];
  locale: Locale;
  currentTeamMemberId?: number;
  currentTeamMemberIds?: readonly number[];
  currentTeamMemberContributorId?: number;
  draftUserId?: Identifier;
  initialEntryIntent?: ProposalVoteFormEntryIntent;
  onEntryIntentChange?: (intent: ProposalVoteFormEntryIntent) => void;
  onOpenChange: (open: boolean) => void;
  onSaved?: (vote: ProposalVoteEntity | undefined) => void;
}

const normalizeText = (value?: string | null) => value?.trim() ?? "";

const getSpeakerName = (member: ActivityMemberEntity, locale: Locale) =>
  [
    localizeFrom(member.eventProfile?.biography?.content?.data?.firstName, locale),
    localizeFrom(member.eventProfile?.biography?.content?.data?.lastName, locale),
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ");

const getSpeakersText = (proposal: ProgramProposalEntity, locale: Locale) =>
  (proposal.members ?? [])
    .filter((member) => member.role === "SPEAKER")
    .map((member) => getSpeakerName(member, locale))
    .filter(Boolean)
    .join(", ");

export const ProposalVoteDialog = ({
  open,
  proposal,
  teamAggregates,
  locale,
  currentTeamMemberId,
  currentTeamMemberIds,
  currentTeamMemberContributorId,
  draftUserId,
  initialEntryIntent,
  onEntryIntentChange,
  onOpenChange,
  onSaved,
}: ProposalVoteDialogProps) => {
  const tAny = useAnyTranslations("features.events-program.ui.tables.eventVotingPanelTable");
  const tProposalDomain = useAnyTranslations("features.proposals.domain");
  const isOpen = open && Boolean(proposal);
  const resolvedCurrentTeamMemberIds =
    currentTeamMemberIds ?? (currentTeamMemberId !== undefined ? [currentTeamMemberId] : undefined);
  const handleDialogOpenChange = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (nextOpen) => {
      onOpenChange(typeof nextOpen === "function" ? nextOpen(isOpen) : nextOpen);
    },
    [isOpen, onOpenChange],
  );
  const title = useMemo(
    () =>
      proposal
        ? normalizeText(localizeFrom(proposal.activity?.content?.data?.title, locale)) ||
          tAny("voteForm.dialog.fallbackTitle")
        : "",
    [locale, proposal, tAny],
  );
  const activityType = proposal?.activity?.typeValue
    ? tProposalDomain(`activity.fields.typeValue.values.${proposal.activity.typeValue}.label`)
    : "";
  const speakersText = useMemo(
    () => (proposal ? getSpeakersText(proposal, locale) : ""),
    [locale, proposal],
  );
  const descriptionParts = [activityType, speakersText].filter(Boolean);
  const description = descriptionParts.join(" · ");
  const formKey = buildProposalVoteFormRemountKey(
    proposal?.id,
    initialEntryIntent,
    proposal?.votes,
  );

  return (
    <AlertDialogWrapper
      title={title}
      description={description}
      isOpen={isOpen}
      setIsOpenCallback={handleDialogOpenChange}
      closeTitle={tAny("voteForm.actions.cancel")}
      classNames={{
        content: "max-h-[calc(100vh-2rem)] gap-0 overflow-hidden p-0 sm:w-3xl lg:w-4xl",
        body: "max-h-[calc(100vh-2rem)] gap-0 overflow-y-auto px-0 pb-0",
        header: "px-6 pt-5 text-left",
        title: "pr-8 text-xl leading-tight",
        description: "border-b px-6 pb-5 text-base",
        footer: "hidden",
      }}
    >
      {proposal ? (
        <div className="space-y-4 px-6 py-5">
          <ProposalVoteFormController
            key={formKey}
            proposal={proposal}
            teamAggregates={teamAggregates}
            currentTeamMemberId={currentTeamMemberId}
            currentTeamMemberIds={resolvedCurrentTeamMemberIds}
            currentTeamMemberContributorId={currentTeamMemberContributorId}
            draftUserId={draftUserId}
            initialEntryIntent={initialEntryIntent}
            onEntryIntentChange={onEntryIntentChange}
            onCancel={() => onOpenChange(false)}
            onSaved={(vote) => {
              onSaved?.(vote);
              onOpenChange(false);
            }}
          />
        </div>
      ) : null}
    </AlertDialogWrapper>
  );
};

ProposalVoteDialog.displayName = "ProposalVoteDialog";
